import { useState } from 'react';
import { Tooltip } from '@mui/material';
import type { ClientRelationWorklog, ClientRelationWorklogDetail } from '../models';
import { formatTotalsToHoursMinutes } from '../helpers/TimeSpanHelper';
import { formatWorklogTime } from '../helpers/WorklogTimeHelper';
import DescriptionIcon from '../icons/DescriptionIcon';
import KeyboardIcon from '../icons/KeyboardIcon';
import MouseIcon from '../icons/MouseIcon';
import NotImageIcon from '../icons/NotImageIcon';
import PointerIcon from '../icons/PointerIcon';
import ProjectIcon from '../icons/ProjectIcon';
import { getPresignedScreenshotUrl } from '../screenshotApi';
import type { TimeFormat, TimeZone } from './TimeSettingsDropDown';

export type WorklogDetailsInfoProps = {
  value: ClientRelationWorklog;
  timeZone: TimeZone;
  timeFormat: TimeFormat;
};

const WorklogDetailsInfo = ({ value, timeZone, timeFormat }: WorklogDetailsInfoProps) => {
  const [popImage, setImage] = useState<string | null>(null);

  const handleImageClick = async (worklogDetails: ClientRelationWorklogDetail) => {
    if (!worklogDetails.Screenshot) {
      setImage(worklogDetails.ScreenshotPreview ?? null);
      return;
    }

    try {
      const url = await getPresignedScreenshotUrl(worklogDetails.Screenshot);
      setImage(url || worklogDetails.ScreenshotPreview || null);
    } catch {
      setImage(worklogDetails.ScreenshotPreview ?? null);
    }
  };

  const getWorklogDetailedTime = (details: ClientRelationWorklogDetail) => {
    if (!details.StartTime || !details.EndTime) return null;

    return (
      <div>
        {formatWorklogTime(details.StartTime, timeZone, timeFormat)} -{' '}
        {formatWorklogTime(details.EndTime, timeZone, timeFormat)}
      </div>
    );
  };

  const getOvertimeClass = (details: ClientRelationWorklogDetail) => {
    if (!details.Overtime) return '';
    return details.OvertimeHours ? 'part-overtime' : 'overtime';
  };

  return (
    <>
      <div className="worklog-details-info-list">
        <div className="worklog-details-info-description">{value.Description}</div>
        <div className="worklog-details-info-cards">
          {value.IsLoggedAutomatically ? (
            value.WorkLogDetails.map((worklogDetails) => (
              <div
                className={`worklog-details-info-card ${getOvertimeClass(worklogDetails)}`}
                key={worklogDetails.CrmWorklogDetailsId}
              >
                <div className="worklog-details-info-card-image">
                  {worklogDetails.ScreenshotPreview ? (
                    <img
                      className="worklog-details-info-card-image-exist"
                      src={worklogDetails.ScreenshotPreview}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleImageClick(worklogDetails)}
                      alt="Screenshot Preview"
                    />
                  ) : (
                    <div
                      className="worklog-details-info-card-image-not-exist"
                      onClick={() => handleImageClick(worklogDetails)}
                    >
                      <p>Screenshot has been deleted</p>
                    </div>
                  )}
                </div>
                <div>
                  {[
                    {
                      icon: KeyboardIcon,
                      dataTrackedLabel: 'Keyboard presses',
                      dataValue: worklogDetails.KeyBoardPressed ?? 0,
                    },
                    {
                      icon: PointerIcon,
                      dataTrackedLabel: 'Mouse clicks',
                      dataValue: worklogDetails.MouseClicks ?? 0,
                    },
                    {
                      icon: MouseIcon,
                      dataTrackedLabel: 'Mouse moves',
                      dataValue: worklogDetails.MouseMoves ?? 0,
                    },
                  ].map((trackedData) => (
                    <div className="tracked-data" key={trackedData.dataTrackedLabel}>
                      <div className="tracked-data-label">
                        <div className="tracker-data-icon">
                          <trackedData.icon />
                        </div>
                        <div className="tracked-data-label-text">
                          {trackedData.dataTrackedLabel}
                        </div>
                      </div>
                      <div>{trackedData.dataValue}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="worklog-details-info-dates">
                    <span>
                      {getWorklogDetailedTime(worklogDetails)}
                      {worklogDetails.Overtime && (
                        <span style={{ paddingLeft: '2px' }}>
                          {worklogDetails.OvertimeHours ? (
                            <Tooltip
                              title={`Overtime: ~${formatTotalsToHoursMinutes(
                                worklogDetails.OvertimeHours
                              )}`}
                              arrow
                            >
                              <div className="part-overtime-tooltip">
                                <div>(Overtime)</div>
                              </div>
                            </Tooltip>
                          ) : (
                            '(Overtime)'
                          )}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div
              className={`worklog-details-info-card ${value.HasOvertime ? 'overtime' : ''} manual`}
            >
              <div className="worklog-details-info-card-manual">
                <div className="worklog-details-info-card-manual-detail">
                  <div className="worklog-details-info-card-manual-detail-label">
                    <div>
                      <ProjectIcon />
                    </div>
                    <div>Project</div>
                  </div>
                  <div className="worklog-details-info-card-manual-detail-value">
                    {value.ProjectName}
                  </div>
                </div>
                <div className="worklog-details-info-card-manual-detail">
                  <div className="worklog-details-info-card-manual-detail-label">
                    <div>
                      <DescriptionIcon />
                    </div>
                    <div>Description</div>
                  </div>
                  <div className="worklog-details-info-card-manual-detail-value">
                    {value.Description}
                  </div>
                </div>
              </div>
              <div className="worklog-details-info-card-manual-screenshot">
                <div>
                  <NotImageIcon />
                </div>
                <div className="worklog-details-info-card-manual-screenshot-text">
                  <p>Screenshot Unavailable</p>
                  <p>This time entry was added manually, so screenshots are not available</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {popImage && (
        <div className="image-modal" onClick={() => setImage(null)}>
          <div className="image-modal-content" onClick={(event) => event.stopPropagation()}>
            <img src={popImage} alt="Full Preview" />
            <button className="modal-close-button" type="button" onClick={() => setImage(null)}>
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default WorklogDetailsInfo;
